/**
 * CopyButton Component
 *
 * Botão para copiar conteúdo do artefato
 */

import React, { memo, useState, useCallback } from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface CopyButtonProps {
  content: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

// =============================================================================
// ICONS
// =============================================================================

const CopyIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect
      x="9"
      y="9"
      width="13"
      height="13"
      rx="2"
      ry="2"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
  </svg>
);

const CheckIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
      fill="currentColor"
    />
  </svg>
);

// =============================================================================
// COMPONENT
// =============================================================================

export const CopyButton: React.FC<CopyButtonProps> = memo(
  ({ content, className = '', size = 'md' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async (e: React.MouseEvent) => {
      e.stopPropagation();

      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        // Silently fail - clipboard API might not be available
      }
    }, [content]);

    // Tamanhos
    const iconSizes = { sm: 14, md: 16, lg: 20 };
    const iconSize = iconSizes[size];

    // Classes de tamanho
    const sizeClasses = {
      sm: 'p-1',
      md: 'p-1.5',
      lg: 'p-2',
    };

    return (
      <button
        onClick={handleCopy}
        className={`
          inline-flex items-center justify-center
          rounded-md transition-all duration-200
          text-gray-400 hover:text-white hover:bg-white/10
          focus:outline-none focus:ring-2 focus:ring-yellow-500/50
          ${copied ? 'text-green-400 bg-green-500/10' : ''}
          ${sizeClasses[size]}
          ${className}
        `}
        aria-label={copied ? 'Copiado!' : 'Copiar conteúdo'}
        title={copied ? 'Copiado!' : 'Copiar conteúdo'}
      >
        {copied ? <CheckIcon size={iconSize} /> : <CopyIcon size={iconSize} />}
      </button>
    );
  }
);

CopyButton.displayName = 'CopyButton';

export default CopyButton;
